/**
 * The OnArk-owned course ids whose guest check-ins are OnArk's OWN marketing
 * funnel — identified to Customer.io and sent follow-up email. A real customer
 * church's guests never reach Customer.io from check-in (see
 * apps/api/src/welcomeAboard/funnelClassCio.ts). Seeded with FIXED ids in
 * every environment; env override only for the rare case an environment needs
 * to point at a different course.
 *
 * Three ids, two roles:
 *   - the Welcome Aboard master (…-0002), the public WA class;
 *   - the BUILD training (…-0001, /onark/training), 14 videos; and
 *   - the INTRO training (…-0004, /onark/online-class), 5 videos.
 *
 * The last two are the same funnel step aimed at different audiences, and both
 * stay live: the intro training is what marketing links to, the build training
 * is what an admin who already pays gets sent to (see ONARK_TRAINING_PATH and
 * ONARK_BUILD_TRAINING_PATH). Both are "the training" everywhere the funnel
 * cares: same Customer.io event stem, same demo milestone, same ladder rung, so
 * a guest who takes either one counts once and is chased once.
 *
 * Shared by the CIO-forwarding gate (apps/api/src/welcomeAboard/config.ts
 * re-exports these getters) and the guest check-in page's "we'll email you"
 * copy, so the two can't drift — a real church's guest must never be told
 * they'll get marketing email that will never come.
 */
export function getWelcomeAboardMasterCourseId(): string {
  return (
    process.env.WELCOME_ABOARD_MASTER_COURSE_ID ||
    'c0a71000-0000-4000-8000-000000000002'
  );
}

// Lives on OnArk's own church, so its check-ins carry the buyer's email but
// NOT their churchId — used to match a check-in to an enrolled church's admin
// and stamp the demo milestone (apps/api/src/graphql/_saveSectionSubmission.ts).
export function getWelcomeAboardDemoCourseId(): string {
  return (
    process.env.WELCOME_ABOARD_DEMO_COURSE_ID ||
    'c0a71000-0000-4000-8000-000000000001'
  );
}

/**
 * The intro training: five short videos, seeded by
 * e2e/training/seed-online-class.ts onto the same OnArk church. The class every
 * MARKETING link points at (the course above still serves logged-in admins).
 */
export function getOnarkOnlineClassCourseId(): string {
  return (
    process.env.ONARK_ONLINE_CLASS_COURSE_ID ||
    'c0a71000-0000-4000-8000-000000000004'
  );
}

/**
 * TWO trainings, two audiences. Picking the wrong one is the mistake this pair
 * of constants exists to prevent, so read both before linking to either.
 *
 * `ONARK_TRAINING_PATH` → /onark/online-class, five short videos. For someone
 * deciding WHETHER to run an online class at all. It assumes no account and no
 * class, and it ends on "Start your free trial". Every marketing surface links
 * here: the footer, the homepage, /features, /pricing, /services, the /start
 * landings, the Welcome Aboard pages, and the /walkthrough redirects.
 *
 * `ONARK_BUILD_TRAINING_PATH` → /onark/training, fourteen videos. For someone
 * who ALREADY HAS AN ACCOUNT and needs to build the thing: orientation,
 * branding, assessments, teams, automations, publishing, running it in person.
 * The in-app admin surfaces link here, and so does the Welcome Aboard status
 * page's demo CTA.
 *
 * The test is the audience, not which class is newer. Sending a paying admin to
 * the intro training ends their training on a pitch for a trial they already
 * converted on; sending a cold prospect to the build training opens with
 * fourteen videos about a product they have not agreed to use yet.
 */
export const ONARK_TRAINING_PATH = '/onark/online-class';

/** See above: the in-app, already-a-customer training. NOT for marketing. */
export const ONARK_BUILD_TRAINING_PATH = '/onark/training';

/**
 * Is this course "the training", either one? Use this rather than comparing
 * against a single id: both stamp the demo milestone and both count as a
 * training rung, because a guest who took either has done the same funnel step.
 */
export function isOnarkTrainingCourseId(
  courseId: string | null | undefined
): boolean {
  if (!courseId) return false;

  return (
    courseId === getWelcomeAboardDemoCourseId() ||
    courseId === getOnarkOnlineClassCourseId()
  );
}

export function isWelcomeAboardFunnelClassCourseId(
  courseId: string | null | undefined
): boolean {
  if (!courseId) return false;

  return (
    courseId === getWelcomeAboardMasterCourseId() ||
    isOnarkTrainingCourseId(courseId)
  );
}

/**
 * The Customer.io-facing name of a funnel class; doubles as the event-name
 * stem (`onark_training_started`, `wa_master_class_completed`, …). Both
 * generations of the training map to `onark_training` on purpose — they are
 * one funnel step, and giving the rebuild its own name would split every
 * campaign, attribute and dashboard already keyed to `*_onark_training`.
 */
export type FunnelClass = 'wa_master_class' | 'onark_training';

/**
 * Which OnArk-owned funnel class a course is, or null for every customer
 * church's course. Lives here (not in the api) because the guest check-in
 * page needs the same answer for the browser half of the Meta lead the api
 * sends the server half of — two copies of this predicate would be two
 * chances for the client and server to disagree about who is a lead.
 */
export function funnelClassForCourse(
  courseId: string | null | undefined
): FunnelClass | null {
  if (!courseId) return null;

  if (courseId === getWelcomeAboardMasterCourseId()) return 'wa_master_class';

  if (isOnarkTrainingCourseId(courseId)) return 'onark_training';

  return null;
}

/**
 * The Meta `content_name` a funnel-class check-in reports as a lead under.
 * A guest checking into one of these classes handed over an email the same
 * way the trial form's step 1 does, so both count as the standard lead event
 * in Meta — the content name is what lets Ads Manager tell the two kinds
 * apart (and both apart from the Welcome Aboard checklist capture). One
 * definition, used by the browser pixel and the server CAPI copy, so the two
 * halves of one de-duplicated event never disagree.
 */
export function funnelClassMetaContentName(cls: FunnelClass): string {
  return cls === 'onark_training'
    ? 'OnArk training check-in'
    : 'Welcome Aboard class check-in';
}

/**
 * Every OnArk-owned funnel course id, for the callers that need to query by
 * `courseId IN (…)` rather than test one id at a time (the ladder-stage guard
 * and the person-level training signals). Kept here so those two cannot drift
 * from the predicate above — they used to build the same array separately, and
 * a new funnel class had to be remembered in three places.
 */
export function getFunnelClassCourseIds(): string[] {
  return [
    getWelcomeAboardMasterCourseId(),
    getWelcomeAboardDemoCourseId(),
    getOnarkOnlineClassCourseId(),
  ].filter((id): id is string => !!id);
}
